import React, { useRef, useState } from 'react';
import { toast } from 'react-toastify';

import {
  downloadCarrierRatesWorkbook,
  useImportCarrierRates,
} from '../../features/carrier-rates/api';

import './BulkCarrierRatesModal.scss';

interface BulkCarrierRatesModalProps {
  open: boolean;
  onClose: () => void;
  onImported?: () => void;
}

export function BulkCarrierRatesModal({ open, onClose, onImported }: BulkCarrierRatesModalProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [isDownloading, setIsDownloading] = useState(false);

  const importCarrierRates = useImportCarrierRates();

  if (!open) {
    return null;
  }

  const handleDownload = async () => {
    try {
      setIsDownloading(true);

      await downloadCarrierRatesWorkbook();

      toast.success('Carrier-rate workbook downloaded.');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Unable to download carrier rates.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;

    if (!file) {
      setSelectedFile(null);
      return;
    }

    const isExcel = file.name.toLowerCase().endsWith('.xlsx');

    if (!isExcel) {
      toast.error('Please select an .xlsx Excel file.');

      event.target.value = '';
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
  };

  const handleImport = async () => {
    if (!selectedFile) {
      toast.warn('Select a completed Excel workbook first.');
      return;
    }

    try {
      const result = await importCarrierRates.mutateAsync(selectedFile);

      toast.success(
        [
          'Carrier rates imported.',
          `${result.created} created`,
          `${result.updated} updated`,
          `${result.removed} removed`,
          `${result.unchanged} unchanged`,
        ].join(' ')
      );

      setSelectedFile(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      onImported?.();
      onClose();
    } catch (error: any) {
      const responseData = error?.response?.data;

      const errors = responseData?.errors as
        | Array<{
            row: number;
            error: string;
          }>
        | undefined;

      if (errors?.length) {
        const firstErrors = errors
          .slice(0, 5)
          .map((entry) => `Row ${entry.row}: ${entry.error}`)
          .join('\n');

        const remaining = errors.length > 5 ? `\n…and ${errors.length - 5} more error(s).` : '';

        toast.error(`${responseData.error}\n${firstErrors}${remaining}`);

        return;
      }

      toast.error(responseData?.error || 'Unable to import carrier rates.');
    }
  };

  const handleClose = () => {
    if (importCarrierRates.isPending) {
      return;
    }

    setSelectedFile(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    onClose();
  };

  return (
    <div
      className="modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="bulk-carrier-rates-title"
    >
      <div className="modal__backdrop" onClick={handleClose} />

      <div className="modal__panel bulk-carrier-rates-modal">
        <div className="modal__header">
          <div>
            <h3 id="bulk-carrier-rates-title" className="modal__title">
              Bulk Carrier Rates
            </h3>

            <p className="muted tiny">
              Download all eligible customer address and warehouse combinations, update the rates in
              Excel, and upload the workbook.
            </p>
          </div>

          <button
            type="button"
            className="btn btn--ghost"
            onClick={handleClose}
            disabled={importCarrierRates.isPending}
          >
            Close
          </button>
        </div>

        <div className="modal__body">
          <section className="bulk-carrier-rates-step">
            <div className="bulk-carrier-rates-step__number">1</div>

            <div className="bulk-carrier-rates-step__content">
              <h4>Download workbook</h4>

              <p className="muted">
                The workbook includes configured rates and blank rows for combinations that have not
                been configured.
              </p>

              <button
                type="button"
                className="btn btn--secondary"
                onClick={handleDownload}
                disabled={isDownloading}
              >
                {isDownloading ? 'Downloading…' : '↓ Download Excel'}
              </button>
            </div>
          </section>

          <section className="bulk-carrier-rates-step">
            <div className="bulk-carrier-rates-step__number">2</div>

            <div className="bulk-carrier-rates-step__content">
              <h4>Update carrier rates</h4>

              <p className="muted">
                Edit only the Carrier Rate ($) column. Blank values remove existing mappings; zero
                remains a configured zero-dollar rate.
              </p>
            </div>
          </section>

          <section className="bulk-carrier-rates-step">
            <div className="bulk-carrier-rates-step__number">3</div>

            <div className="bulk-carrier-rates-step__content">
              <h4>Upload workbook</h4>

              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={handleFileChange}
              />

              {selectedFile && (
                <div className="bulk-carrier-rates-file">
                  <span>{selectedFile.name}</span>

                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => {
                      setSelectedFile(null);

                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    disabled={importCarrierRates.isPending}
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          </section>
        </div>

        <div className="modal__footer">
          <button
            type="button"
            className="btn btn--ghost"
            onClick={handleClose}
            disabled={importCarrierRates.isPending}
          >
            Cancel
          </button>

          <button
            type="button"
            className="btn btn--primary"
            onClick={handleImport}
            disabled={!selectedFile || importCarrierRates.isPending}
          >
            {importCarrierRates.isPending ? 'Importing…' : 'Upload and Apply'}
          </button>
        </div>
      </div>
    </div>
  );
}
